<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon>
                    <StripeLogo />
                </template>
                <DialogTitle>
                    {{
                        integration.status !== "active"
                            ? $t("integrations.stripe.modal.finish_integration")
                            : $t("integrations.stripe.modal.title_edit")
                    }}</DialogTitle
                >
            </DialogHeader>

            <!-- Integration is incomplete -->
            <div class="px-6" v-if="integration.status !== 'active'">
                <Alert variant="error">
                    {{ $t("integrations.stripe.modal.incomplete") }}
                </Alert>
            </div>

            <!-- Integration is active -->
            <div
                v-auto-animate
                class="mt-4 gap-3 px-6 grid"
                v-if="integration.status === 'active'"
            >
                <p class="text-sm text-iron-500 tracking-[-0.01em]">
                    {{ $t("integrations.stripe.modal.description") }}
                </p>

                <div class="mt-4 gap-4 grid grid-cols-2">
                    <div class="gap-1 grid grid-cols-1">
                        <Label>{{ $t("integrations.stripe.modal.crn") }}</Label>
                        <Input v-model="crn"></Input>
                    </div>
                    <div class="gap-1 grid grid-cols-1">
                        <Label>{{
                            $t("integrations.stripe.modal.tax_id")
                        }}</Label>
                        <Input v-model="taxId"></Input>
                    </div>
                </div>

                <div v-if="locale === 'cs'" class="mt-4 gap-1 grid grid-cols-1">
                    <Label>{{
                        $t("integrations.stripe.modal.tax_registration")
                    }}</Label>

                    <Select v-model="taxRegistration">
                        <SelectTrigger>
                            <SelectValue
                                placeholder="Vybrat registraci k dani"
                            />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectItem value="non-tax-payer"
                                    >Neplátce DPH</SelectItem
                                >
                                <SelectItem value="tax-payer"
                                    >Plátce DPH</SelectItem
                                >
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div
                    v-auto-animate
                    v-if="crn || taxId"
                    class="gap-1 grid grid-cols-1"
                >
                    <a
                        href="https://dashboard.stripe.com/tax/registrations"
                        target="_blank"
                        v-if="taxRegistration === 'tax-payer'"
                        class="mt-3 rounded-md border-iron-300 bg-white px-5 py-3 text-base text-iron-700 shadow-sm hover:bg-iron-50 focus:ring-z-orange-500 relative inline-flex w-full border focus:ring-2 focus:ring-offset-2 focus:outline-none"
                    >
                        <ExternalLink
                            class="right-3 top-3 absolute"
                            :size="16"
                        />
                        <p class="font-medium text-left">
                            {{
                                $t("integrations.stripe.modal.vat_registration")
                            }}
                        </p>
                    </a>
                    <a
                        href="https://dashboard.stripe.com/settings/update/public/support-details"
                        target="_blank"
                        class="mt-2 rounded-md border-iron-300 bg-white px-4 py-2 text-base text-iron-700 shadow-sm hover:bg-iron-50 focus:ring-z-orange-500 relative inline-flex w-full border focus:ring-2 focus:ring-offset-2 focus:outline-none"
                    >
                        <ExternalLink
                            class="right-3 top-3 absolute"
                            :size="16"
                        />
                        <div>
                            <p class="font-medium text-left">
                                {{
                                    $t(
                                        "integrations.stripe.modal.billing_details.title",
                                    )
                                }}
                            </p>

                            <p class="mt-1 text-sm">
                                {{
                                    $t(
                                        "integrations.stripe.modal.billing_details.description",
                                    )
                                }}
                            </p>
                        </div>
                    </a>
                </div>

                <div class="mt-2 flex justify-start">
                    <StripeExampleInvoice
                        v-if="locale === 'cs'"
                        :crn="crn"
                        :tax-id="taxId"
                        :is-tax-payer="taxRegistration === 'tax-payer'"
                        :integration-id="props.integration.id"
                    >
                        <Button variant="ghost" class="gap-2" size="xs">
                            <FileText :size="20" />
                            Zobrazit příklad faktury
                        </Button>
                    </StripeExampleInvoice>
                </div>
            </div>

            <DialogFooter v-if="integration.status === 'active'">
                <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>

                <DialogClose>
                    <Button variant="outline" type="button">{{
                        $t("cancel")
                    }}</Button>
                </DialogClose>

                <Button @click="save" :disabled="loading">
                    <span v-if="!loading"> {{ $t("save_changes") }}</span>
                    <span v-if="loading">{{ $t("saving") }}...</span>
                </Button>
            </DialogFooter>

            <DialogFooter v-if="integration.status !== 'active'">
                <DialogClose>
                    <Button variant="outline" type="button">{{
                        $t("cancel")
                    }}</Button>
                </DialogClose>

                <Button
                    as="a"
                    :href="
                        route('app.stripe-integrations.refresh', {
                            integration: props.integration.id,
                        })
                    "
                >
                    {{ $t("continue") }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExternalLink, FileText, Trash } from "lucide-vue-next";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import { vAutoAnimate } from "@formkit/auto-animate/vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";

import { useI18n } from "vue-i18n";
import StripeExampleInvoice from "./StripeExampleInvoice.vue";
import StripeLogo from "../../logos/simple/Stripe.svg";
import { Alert } from "@/components/ui/alert";

defineEmits(["remove-integration"]);

const props = defineProps<{
    removable?: boolean;
    integration?: any;
    open?: boolean;
}>();

const crn = ref("");
const taxId = ref("");
const taxRegistration = ref<"tax-payer" | "non-tax-payer">();
const opened = ref(props.open ? props.open : false);

const { locale } = useI18n();

const loading = ref(false);

function updateTaxIds({ close = false }: { close: boolean }) {
    router.post(
        route("app.stripe-integrations.tax-ids.store", {
            integration: props.integration.id,
        }),
        {
            taxId: taxId.value,
            crn: crn.value,
            taxPayer: isTaxPayer(),
        },
        {
            onSuccess: () => {
                if (close) {
                    opened.value = false;
                }

                loading.value = false;
            },
        },
    );
}

function isTaxPayer() {
    if (locale.value === "cs") {
        return taxRegistration.value === "tax-payer";
    }

    return null;
}

function save() {
    loading.value = true;
    updateTaxIds({ close: false });
}

onMounted(async () => {
    if (props.integration) {
        crn.value = props.integration.data.crn;
        taxId.value = props.integration.data?.tax_id?.value;
        taxRegistration.value = props.integration.data.taxPayer
            ? "tax-payer"
            : "non-tax-payer";
    }
});
</script>

<style></style>
